import type { Canvas2DLike } from "../canvas/canvas-types";

/** Catmull-Rom tension. 0.5 matches the legacy `LonaCanvasContext`. */
const TENSION = 0.5;

/**
 * Trace a smooth Catmull-Rom spline through `pts` (already in canvas space) on
 * an open path. This is the smoothing the legacy `LineChartBody` used —
 * tension-0.5 Catmull-Rom tangents converted to cubic-Bézier control points
 * (ported from `tento-ui` `extended-canvas-context.ts`, the
 * `LonaCanvasContext` curve renderer). The `@tento-data-ui` renderers dropped
 * it in the migration, which is why the regression curve/band/fill read as
 * faceted polylines.
 *
 * With fewer than 3 points there is nothing to curve, so it falls back to
 * straight segments. Pass `continuePath = true` to append to an already-open
 * subpath (the band ribbon and the fill polygon stitch two spline edges into
 * one path) — it then starts with `lineTo` instead of `moveTo`.
 */
export function smoothThrough(
  ctx: Canvas2DLike,
  pts: readonly (readonly [number, number])[],
  continuePath = false,
): void {
  const start = pts[0];
  if (start === undefined) return;

  if (continuePath) ctx.lineTo(start[0], start[1]);
  else ctx.moveTo(start[0], start[1]);

  if (pts.length < 3) {
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]![0], pts[i]![1]);
    return;
  }

  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[Math.min(pts.length - 1, i + 2)]!;

    const cp1x = p1[0] + (TENSION * (p2[0] - p0[0])) / 3;
    const cp1y = p1[1] + (TENSION * (p2[1] - p0[1])) / 3;
    const cp2x = p2[0] - (TENSION * (p3[0] - p1[0])) / 3;
    const cp2y = p2[1] - (TENSION * (p3[1] - p1[1])) / 3;

    ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2[0], p2[1]);
  }
}
